/**
 * One team: who is in it, and who else could be.
 *
 * Membership is managed here rather than from the Members tab in Settings,
 * because the question people arrive with is "who is on Growth", not "which
 * teams is Dana on". The inverse view is worth adding when somebody asks for it.
 *
 * The candidate list is the organization's members minus the ones already here.
 * Rendering it as a picker rather than asking for a user id is the difference
 * between a screen an administrator can use and one that needs the API docs
 * open beside it.
 */

import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { api, ApiError } from "../api/client";
import { useAsync } from "../hooks";
import { SkeletonRows } from "../components/Loading";
import { Icon } from "../components/Sprites";
import { roleLabel } from "../roles";
import { useSession } from "../session";
import type { CursorPage, Member, Role, Team } from "../api/types";

export function TeamDetail() {
  const { teamId = "" } = useParams();
  const { orgId, can, session } = useSession();
  const navigate = useNavigate();
  const manageable = can("teams:manage");

  const team = useAsync<Team>(
    (signal) => api.get<Team>(`/v1/teams/${encodeURIComponent(teamId)}`, { signal }),
    [orgId, teamId],
  );

  const members = useAsync<CursorPage<Member>>(
    (signal) =>
      api.get<CursorPage<Member>>(`/v1/teams/${encodeURIComponent(teamId)}/members`, {
        signal,
        query: { limit: 100 },
      }),
    [orgId, teamId],
  );

  // Everyone in the organization, for two things: offering the people not yet
  // here, and supplying their organization role.
  //
  // The role has to come from here because the team listing deliberately omits
  // it — a role belongs to the organization, not to a team, and the endpoint
  // declines to imply otherwise. That is the right call on the wire and leaves
  // this screen to do the join, which it can, since it needs the same list
  // anyway. Fetched for everybody rather than only for managers, or the column
  // would be a row of dashes for anyone who cannot edit.
  const everyone = useAsync<CursorPage<Member>>(
    (signal) =>
      api.get<CursorPage<Member>>("/v1/organization/members", { signal, query: { limit: 200 } }),
    [orgId],
  );

  if (team.error) {
    return (
      <>
        <div className="topbar">
          <h1 className="oc-headline-lg">Team</h1>
        </div>
        <div className="content">
          <div className="oc-panel oc-starfield" style={{ flex: 1 }}>
            <div className="oc-empty">
              <div className="oc-empty__title">Could not load this team</div>
              <div className="oc-empty__hint">{team.error.message}</div>
              <button type="button" className="oc-btn oc-btn--sm" onClick={() => navigate("/teams")}>
                Back to teams
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  const rows = members.data?.data ?? [];
  const inTeam = new Set(rows.map((m) => m.user_id));
  const candidates = (everyone.data?.data ?? []).filter((m) => !inTeam.has(m.user_id));

  // The organization role, by user id, for the column the team listing leaves
  // empty on purpose.
  const orgRoles = new Map((everyone.data?.data ?? []).map((m) => [m.user_id, m.role]));

  const reload = () => {
    void members.reload();
  };

  return (
    <>
      <div className="topbar">
        <div className="row" style={{ gap: "var(--oc-space-3)", minWidth: 0 }}>
          <button
            type="button"
            className="oc-btn oc-btn--ghost oc-btn--icon oc-btn--sm"
            aria-label="Back to teams"
            onClick={() => navigate("/teams")}
          >
            <Icon name="chevron" size={14} className="icon--back" />
          </button>
          <h1 className="oc-headline-lg oc-truncate">{team.data?.name ?? "Team"}</h1>
        </div>
        <div className="row">
          {manageable && candidates.length > 0 && (
            <AddMember teamId={teamId} candidates={candidates} onAdded={reload} />
          )}
          <button type="button" className="oc-btn oc-btn--ghost oc-btn--sm" onClick={reload}>
            <Icon name="refresh" size={12} />
            Refresh
          </button>
        </div>
      </div>

      <div className="content">
        <div className="oc-panel" style={{ padding: "var(--oc-space-4)" }}>
          <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
            These people see the templates, campaigns and groups assigned to this team.
            Owners and admins see them regardless of membership; everybody else sees only
            their own teams' work plus whatever is left unassigned.
          </p>
        </div>

        <div className="oc-panel oc-starfield" style={{ flex: 1, minWidth: 0 }}>
          <div className="oc-table team-members-table" style={{ flex: 1, minHeight: 0 }}>
            <div className="oc-table__head" role="row">
              <span>Person</span>
              <span>Organization role</span>
              <span className="oc-table__cell--right">Action</span>
            </div>

            <div className="oc-table__body">
              {members.error && (
                <p className="field-error" style={{ padding: "var(--oc-gutter)" }}>
                  {members.error.message}
                </p>
              )}

              {members.loading && rows.length === 0 && <SkeletonRows columns={3} />}

              {!members.error && rows.length === 0 && !members.loading && (
                <div className="oc-empty">
                  <div className="oc-empty__title">Nobody in this team</div>
                  <div className="oc-empty__hint">
                    An empty team hides its templates and campaigns from everyone except
                    owners and admins. Add somebody, or leave the work unassigned so the
                    whole organization can see it.
                  </div>
                </div>
              )}

              {rows.map((member) => (
                <MemberRow
                  key={member.user_id}
                  teamId={teamId}
                  member={member}
                  isSelf={member.user_id === session?.user.id}
                  orgRole={orgRoles.get(member.user_id)}
                  removable={manageable}
                  onRemoved={reload}
                />
              ))}
            </div>

            <div className="oc-table__footer">
              <span className="oc-mono-sm">
                {rows.length === 0 ? "No members" : `${rows.length} member${rows.length === 1 ? "" : "s"}`}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function MemberRow({
  teamId,
  member,
  isSelf,
  orgRole,
  removable,
  onRemoved,
}: {
  teamId: string;
  member: Member;
  isSelf: boolean;
  orgRole?: Role;
  removable: boolean;
  onRemoved: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.delete(`/v1/teams/${encodeURIComponent(teamId)}/members/${member.user_id}`);
      onRemoved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not reach the server.");
      setBusy(false);
    }
  };

  return (
    <div className="oc-table__row" role="row">
      <span className="oc-body-sm oc-truncate">
        {member.name || member.email}
        {isSelf && <span className="oc-text-muted-aa"> · you</span>}
        {member.name && <span className="oc-text-secondary"> — {member.email}</span>}
        {error && (
          <span className="field-error" role="alert">
            {error}
          </span>
        )}
      </span>
      {/* The organization role, labelled as such. A team carries no role of its
          own, and showing a bare "Developer" in a team screen invites reading it
          as one — which would be the exact confusion between seeing and doing
          that the panel above warns about. */}
      <span className="oc-body-sm oc-text-secondary">
        {orgRole ? roleLabel(orgRole) : "—"}
      </span>
      <span className="oc-table__cell--right">
        {removable && (
          <button
            type="button"
            className="oc-btn oc-btn--ghost oc-btn--xs"
            disabled={busy}
            onClick={() => {
              // Removing yourself is allowed and worth a warning, because the
              // screen you are on may vanish: outside the team, a non-admin
              // stops seeing its work entirely.
              const message = isSelf
                ? `Remove yourself from this team?\n\nUnless you are an owner or admin, you will stop seeing its templates, campaigns and groups.`
                : `Remove ${member.email} from this team?`;
              if (!window.confirm(message)) return;
              void remove();
            }}
          >
            {busy ? "Removing…" : "Remove"}
          </button>
        )}
      </span>
    </div>
  );
}

/**
 * Add somebody, from the people already in the organization.
 *
 * A team can only contain organization members — the endpoint enforces it — so
 * offering a free-text address would invite typing one that cannot be added and
 * reading the refusal as a bug. Invite them to the organization first, then
 * they appear here.
 */
function AddMember({
  teamId,
  candidates,
  onAdded,
}: {
  teamId: string;
  candidates: Member[];
  onAdded: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const add = async () => {
    if (!userId) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/v1/teams/${encodeURIComponent(teamId)}/members`, { user_id: userId });
      setUserId("");
      setOpen(false);
      onAdded();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not reach the server.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        className="oc-btn oc-btn--primary oc-btn--sm"
        onClick={() => setOpen(true)}
      >
        Add member
      </button>
    );
  }

  return (
    <div className="row">
      <select
        className="oc-input oc-input--sm"
        aria-label="Person to add"
        autoFocus
        value={userId}
        onChange={(event) => setUserId(event.target.value)}
      >
        <option value="">Choose someone…</option>
        {candidates.map((m) => (
          <option key={m.user_id} value={m.user_id}>
            {m.name ? `${m.name} (${m.email})` : m.email}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="oc-btn oc-btn--primary oc-btn--sm"
        disabled={busy || userId === ""}
        onClick={() => void add()}
      >
        {busy ? "Adding…" : "Add"}
      </button>
      <button
        type="button"
        className="oc-btn oc-btn--ghost oc-btn--sm"
        onClick={() => {
          setOpen(false);
          setError(null);
        }}
      >
        Cancel
      </button>
      {error && (
        <span className="field-error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
